// ============================================================================
// TEMPLATE CONTEXT
// Shared state for session templates across the application
// ============================================================================

import React, { createContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { SessionTemplate, PhaseTemplate } from '../types';
import { browseSessionTemplates, browsePhaseTemplates } from '../services/templateService';

interface TemplateContextType {
  // Session Templates
  currentTemplate: SessionTemplate | null;
  sessionTemplates: SessionTemplate[];
  defaultTemplate: SessionTemplate | null;
  loadingSessionTemplates: boolean;
  sessionTemplatesError: string | null;

  // Phase Templates
  phaseTemplates: PhaseTemplate[];
  loadingPhaseTemplates: boolean;
  phaseTemplatesError: string | null;

  // Actions
  setCurrentTemplate: (template: SessionTemplate | null) => void;
  refreshSessionTemplates: () => Promise<void>;
  refreshPhaseTemplates: () => Promise<void>;
  getTemplateById: (templateId: string) => SessionTemplate | undefined;
  getPhaseTemplateById: (phaseTemplateId: string) => PhaseTemplate | undefined;
}

const TemplateContext = createContext<TemplateContextType | undefined>(undefined);

interface TemplateProviderProps {
  children: ReactNode;
}

export function TemplateProvider({ children }: TemplateProviderProps) {
  // Session Templates State
  const [currentTemplate, setCurrentTemplate] = useState<SessionTemplate | null>(null);
  const [sessionTemplates, setSessionTemplates] = useState<SessionTemplate[]>([]);
  const [defaultTemplate, setDefaultTemplate] = useState<SessionTemplate | null>(null);
  const [loadingSessionTemplates, setLoadingSessionTemplates] = useState<boolean>(true);
  const [sessionTemplatesError, setSessionTemplatesError] = useState<string | null>(null);

  // Phase Templates State
  const [phaseTemplates, setPhaseTemplates] = useState<PhaseTemplate[]>([]);
  const [loadingPhaseTemplates, setLoadingPhaseTemplates] = useState<boolean>(true);
  const [phaseTemplatesError, setPhaseTemplatesError] = useState<string | null>(null);

  /**
   * Fetch session templates
   */
  const refreshSessionTemplates = async () => {
    setLoadingSessionTemplates(true);
    setSessionTemplatesError(null);

    try {
      // Fetch all approved templates for current user/org
      const result = await browseSessionTemplates({
        limit: 100 // Fetch up to 100 templates
      });

      setSessionTemplates(result.templates);

      // Find default template
      const defaultTpl = result.templates.find(t => t.isDefault);
      if (defaultTpl) {
        setDefaultTemplate(defaultTpl);
      }
    } catch (error: any) {
      console.error('Failed to load session templates:', error);
      setSessionTemplatesError(error.message);
    } finally {
      setLoadingSessionTemplates(false);
    }
  };

  /**
   * Fetch phase templates
   */
  const refreshPhaseTemplates = async () => {
    setLoadingPhaseTemplates(true);
    setPhaseTemplatesError(null);

    try {
      // Fetch all approved phase templates
      const result = await browsePhaseTemplates({
        limit: 100 // Fetch up to 100 phase templates
      });

      setPhaseTemplates(result.templates);
    } catch (error: any) {
      console.error('Failed to load phase templates:', error);
      setPhaseTemplatesError(error.message);
    } finally {
      setLoadingPhaseTemplates(false);
    }
  };

  /**
   * Get template by ID (latest version)
   */
  const getTemplateById = (templateId: string): SessionTemplate | undefined => {
    return sessionTemplates.find(t => t.sessionTemplateId === templateId);
  };

  /**
   * Get phase template by ID (latest version)
   */
  const getPhaseTemplateById = (phaseTemplateId: string): PhaseTemplate | undefined => {
    return phaseTemplates.find(pt => pt.phaseTemplateId === phaseTemplateId);
  };

  // Load templates on mount
  useEffect(() => {
    refreshSessionTemplates();
    refreshPhaseTemplates();
  }, []);

  const value = useMemo<TemplateContextType>(() => ({
    currentTemplate,
    sessionTemplates,
    defaultTemplate,
    loadingSessionTemplates,
    sessionTemplatesError,
    phaseTemplates,
    loadingPhaseTemplates,
    phaseTemplatesError,
    setCurrentTemplate,
    refreshSessionTemplates,
    refreshPhaseTemplates,
    getTemplateById,
    getPhaseTemplateById
  }), [currentTemplate, sessionTemplates, defaultTemplate, loadingSessionTemplates, sessionTemplatesError, phaseTemplates, loadingPhaseTemplates, phaseTemplatesError, setCurrentTemplate, refreshSessionTemplates, refreshPhaseTemplates, getTemplateById, getPhaseTemplateById]);

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
}

